import { useMutation } from "@apollo/client/react";
import moment from "moment";
import { useCallback } from "react";

import { useRecordingConsent } from "../components/consent/useRecordingConsent";
import {
  parseJobType,
  JOB_TYPE_KIND,
} from "../components/dashboard/utils";
import {
  CREATE_QUICK_NOTE,
  CREATE_PATIENT_NOTE,
  CREATE_GROUPED_NOTES,
} from "../components/notes/edit/api/mutations";

export function useNoteCreation({ variant = "quick" } = {}) {
  const { needsConsent } = useRecordingConsent();
  const [createQuickNote] = useMutation(CREATE_QUICK_NOTE);
  const [createPatientNote] = useMutation(CREATE_PATIENT_NOTE);
  const [createGroupedNotes] = useMutation(CREATE_GROUPED_NOTES);

  return useCallback(
    async ({
      audioStorageLink,
      storageObjectName,
      jobType,
      title,
      recordingAs,
      teamUuid,
      patientUuid,
      recordingConsentEventUuid,
    }) => {
      // Single enforcement point for recording consent. Capture-time gating
      // always supplies this uuid; its absence here means a caller reached note
      // creation without passing the gate — fail loudly rather than persist an
      // unlinked note. Append/manual notes never route through this hook.
      if (needsConsent && !recordingConsentEventUuid) {
        throw new Error(
          "Refusing to create a recording note without a recording-consent event.",
        );
      }

      const parsed = parseJobType(jobType);
      const isPatient = variant === "patient";

      if (parsed.kind === JOB_TYPE_KIND.TEMPLATE_GROUP) {
        await createGroupedNotes({
          variables: {
            storageObjectName,
            templateGroupUuid: parsed.templateGroupUuid,
            ...(isPatient &&
              patientUuid !== undefined && { patientUuid }),
            ...(title !== undefined && { title }),
            ...(recordingConsentEventUuid && {
              recordingConsentEventUuid,
            }),
          },
        });
        return null;
      }

      const now = moment().format();
      const noteInput = {
        audioStorageLink,
        storageObjectName,
        createdAt: now,
        lastUpdatedAt: now,
        jobType: parsed.jobType,
        ...(parsed.templateUuid && {
          templateUuid: parsed.templateUuid,
        }),
        ...(title !== undefined && { title }),
      };

      if (isPatient) {
        if (patientUuid !== undefined) {
          noteInput.patientUuid = patientUuid;
        }
        const result = await createPatientNote({
          variables: {
            noteInput,
            ...(recordingConsentEventUuid && {
              recordingConsentEventUuid,
            }),
          },
        });
        return result?.data?.createPatientNote?.note ?? null;
      }

      if (recordingAs !== undefined) {
        noteInput.recordingAs = recordingAs;
      }
      if (teamUuid !== undefined) {
        noteInput.teamUuid = teamUuid;
      }

      const result = await createQuickNote({
        variables: {
          noteInput,
          ...(recordingConsentEventUuid && {
            recordingConsentEventUuid,
          }),
        },
      });
      return result?.data?.createQuickNote?.note ?? null;
    },
    [
      variant,
      needsConsent,
      createQuickNote,
      createPatientNote,
      createGroupedNotes,
    ],
  );
}
